import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function IntrinsicValueLesson({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              Intermediate
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              20 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            💎 Intrinsic Value & Stock Valuation
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Learn how to calculate what a stock is actually worth using DCF and other methods
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">What is Intrinsic Value?</h2>
              <p className="mb-4">
                Imagine you're buying a fruit shop. You'd want to know: How much profit does it make? Will it keep making money? 
                Based on that, you'd decide what price to pay. <strong>That's intrinsic value</strong> - the true worth of a business based on its future cash flows.
              </p>
              <p className="mb-4">
                Warren Buffett famously said: <em>"Price is what you pay, value is what you get."</em> 
                Market price changes every second based on emotions. Intrinsic value is calculated using facts and logic.
              </p>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm">
                  <strong>🎯 Goal of Value Investing:</strong> Buy stocks when market price is below intrinsic value (undervalued). 
                  Avoid when market price is above intrinsic value (overvalued).
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The Discounted Cash Flow (DCF) Method</h2>
              <p className="mb-4">
                DCF is the most scientific way to value a company. It answers: <strong>"What is the present value of all future cash this business will generate?"</strong>
              </p>
              
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">DCF Logic in Simple Terms:</p>
                <p className="text-sm mb-2">
                  If someone promises to pay you ₹100 after 1 year, you wouldn't pay ₹100 today, right? 
                  You'd pay maybe ₹90 (because money today is more valuable than money tomorrow).
                </p>
                <p className="text-sm">
                  DCF does exactly this: Estimate future cash flows and "discount" them to today's value using a discount rate.
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">DCF Steps Explained with Reliance Example</h3>
                
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <p className="font-semibold text-gray-800 dark:text-gray-100">Step 1: Estimate Future Free Cash Flows (FCF)</p>
                    <p className="text-sm">Look at Reliance's current free cash flow (₹50,000 Cr) and project it for next 5-10 years based on expected growth (say 15%).</p>
                    <p className="text-sm font-mono mt-1">
                      Year 1: ₹57,500 Cr | Year 2: ₹66,125 Cr | Year 3: ₹76,043 Cr...
                    </p>
                  </div>

                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                    <p className="font-semibold text-gray-800 dark:text-gray-100">Step 2: Choose a Discount Rate (WACC)</p>
                    <p className="text-sm">This is your required rate of return. Typically 10-15% for Indian stocks. Let's use 12%.</p>
                    <p className="text-sm mt-1">Higher discount rate = Lower valuation (more conservative)</p>
                  </div>

                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded">
                    <p className="font-semibold text-gray-800 dark:text-gray-100">Step 3: Discount Future Cash Flows to Present Value</p>
                    <p className="text-sm">Year 1 PV: ₹57,500 / (1.12)^1 = ₹51,339 Cr</p>
                    <p className="text-sm">Year 2 PV: ₹66,125 / (1.12)^2 = ₹52,697 Cr</p>
                    <p className="text-sm font-mono">... and so on for all years</p>
                  </div>

                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                    <p className="font-semibold text-gray-800 dark:text-gray-100">Step 4: Calculate Terminal Value</p>
                    <p className="text-sm">After Year 10, the company still has value! Use perpetual growth rate (say 4% = India's GDP growth).</p>
                    <p className="text-sm font-mono">Terminal Value = FCF Year 11 / (Discount Rate - Growth Rate)</p>
                  </div>

                  <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded">
                    <p className="font-semibold text-gray-800 dark:text-gray-100">Step 5: Sum Everything & Divide by Shares</p>
                    <p className="text-sm">Total Enterprise Value = Sum of all PVs + Terminal Value</p>
                    <p className="text-sm">Intrinsic Value per Share = Enterprise Value / Total Shares</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Real Example: Is TCS Overvalued or Undervalued?</h2>
              
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Current Market Data (Example):</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>TCS Share Price: <strong>₹3,500</strong></div>
                  <div>Current FCF: <strong>₹40,000 Cr</strong></div>
                  <div>Expected Growth: <strong>12%</strong></div>
                  <div>Discount Rate: <strong>12%</strong></div>
                  <div>Terminal Growth: <strong>4%</strong></div>
                  <div>Total Shares: <strong>365 Cr</strong></div>
                </div>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">DCF Calculation Result:</p>
                <p className="text-sm mb-1">PV of 10-year cash flows: ₹4,50,000 Cr</p>
                <p className="text-sm mb-1">PV of Terminal Value: ₹8,00,000 Cr</p>
                <p className="text-sm mb-2">Total Enterprise Value: ₹12,50,000 Cr</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">Intrinsic Value per Share: ₹3,424</p>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded border-l-4 border-yellow-500">
                <p className="text-sm">
                  <strong>Analysis:</strong> Market Price = ₹3,500 | Intrinsic Value = ₹3,424
                </p>
                <p className="text-sm mt-2">
                  <strong>Verdict:</strong> TCS is trading slightly above its intrinsic value (overvalued by ~2%). 
                  Conservative investors might wait for a better entry point around ₹3,200-3,300.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Other Valuation Methods</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">1. P/E Multiple Method (Simpler)</h4>
                  <p className="text-sm mb-2">Fair Value = EPS × Industry Average P/E</p>
                  <p className="text-sm">Example: If TCS EPS = ₹120 and IT sector P/E = 25, Fair Value = ₹3,000</p>
                  <p className="text-sm mt-2"><strong>Pro:</strong> Quick and easy | <strong>Con:</strong> Less accurate than DCF</p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">2. Graham Number (Conservative)</h4>
                  <p className="text-sm mb-2">Fair Value = √(22.5 × EPS × Book Value per Share)</p>
                  <p className="text-sm">Benjamin Graham's formula for maximum price to pay for a stock.</p>
                  <p className="text-sm mt-2"><strong>Pro:</strong> Very conservative, safe | <strong>Con:</strong> Often too low for growth stocks</p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">3. Dividend Discount Model (DDM)</h4>
                  <p className="text-sm mb-2">Fair Value = Next Year Dividend / (Discount Rate - Dividend Growth Rate)</p>
                  <p className="text-sm">Best for stable, dividend-paying stocks like HDFC Bank, ITC.</p>
                  <p className="text-sm mt-2"><strong>Pro:</strong> Good for dividend stocks | <strong>Con:</strong> Doesn't work for non-dividend payers</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The Margin of Safety Principle</h2>
              <p className="mb-4">
                Even if you calculate intrinsic value perfectly, never pay the full price! Always demand a <strong>margin of safety</strong>.
              </p>
              
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Benjamin Graham's Rule:</p>
                <p className="text-sm mb-2">
                  <strong>"Never buy a stock unless it's trading at least 20-30% below your calculated intrinsic value."</strong>
                </p>
                <p className="text-sm">
                  If intrinsic value is ₹3,000, wait for price to drop to ₹2,400 or below. This margin protects you from calculation errors and unforeseen problems.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Scenario</th>
                      <th className="p-3 text-right">Intrinsic Value</th>
                      <th className="p-3 text-right">Market Price</th>
                      <th className="p-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/10">
                      <td className="p-3">Highly Undervalued</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹2,000</td>
                      <td className="p-3 text-center font-bold text-green-600">✅ Strong Buy</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/5">
                      <td className="p-3">Undervalued</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹2,500</td>
                      <td className="p-3 text-center font-bold text-green-600">✅ Buy</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Fairly Valued</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹2,900</td>
                      <td className="p-3 text-center font-bold text-yellow-600">⚠️ Hold</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-red-50 dark:bg-red-900/10">
                      <td className="p-3">Overvalued</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹3,500</td>
                      <td className="p-3 text-center font-bold text-red-600">❌ Avoid</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Limitations of Valuation Models</h2>
              
              <div className="space-y-3">
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-sm"><strong>⚠️ Garbage In, Garbage Out:</strong> If your growth assumptions are wrong, your valuation will be wrong. Be conservative!</p>
                </div>
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-sm"><strong>⚠️ Doesn't work for startups:</strong> No cash flows yet? Can't use DCF. Valuation is more art than science here.</p>
                </div>
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                  <p className="text-sm"><strong>⚠️ Market can stay irrational:</strong> Even if you're right about value, market might stay overvalued for years (tech stocks 2021!)</p>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded mt-4">
                <p className="text-sm">
                  <strong>Warren Buffett's Wisdom:</strong> "It's better to be approximately right than precisely wrong." 
                  Don't get lost in complex models. Focus on quality businesses bought at reasonable prices.
                </p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Key Takeaways</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Intrinsic value = True worth</strong> of a business based on future cash flows, not market emotions</li>
                <li><strong>DCF is the gold standard:</strong> Most scientific method, but requires careful assumptions</li>
                <li><strong>Always demand margin of safety:</strong> Buy 20-30% below intrinsic value to protect against errors</li>
                <li><strong>Use multiple methods:</strong> DCF, P/E method, Graham Number - cross-check your valuation</li>
                <li><strong>Be conservative:</strong> Better to miss an opportunity than lose capital on overvalued stocks</li>
                <li><strong>Try our calculator:</strong> Use the Intrinsic Value Calculator on the Learn page to practice!</li>
              </ul>
            </div>

          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn/key-metrics')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: Key Metrics
          </Button>
          <Button 
            onClick={() => navigate('/learn/buffett-philosophy')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
          >
            Next: Buffett's Philosophy
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
